import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import { Create as CreateIcon } from '@mui/icons-material';
import { getFileUrl, templateApi } from '../services/api';
import { Template } from '../types/template';

export const TemplateDetails: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await templateApi.getTemplate(templateId!);
        setTemplate(response.data);
      } catch (err) {
        setError('Failed to load template details');
        console.error('Error fetching template:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [templateId]);

  const handleUseTemplate = () => {
    navigate(`/create/${templateId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !template) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error" align="center">
          {error || 'Template not found'}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <Grid container spacing={4}>
        {/* Image Section */}
        <Grid item xs={12} md={7}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: 'background.paper',
            }}
          >
            <img
              src={getFileUrl(template.imageUrl)}
              alt={template.title}
              style={{
                width: '100%',
                maxHeight: '600px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </Paper>
        </Grid>

        {/* Details Section */}
        <Grid item xs={12} md={5}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="h4" gutterBottom>
              {template.title}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Description
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {template.description}
              </Typography>
            </Box>

            {template.metadata?.tags && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Tags
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {template.metadata.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.main',
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Box sx={{ mt: 'auto', pt: 3 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<CreateIcon />}
                onClick={handleUseTemplate}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Use This Template
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}; 